import React, { useEffect, useMemo, useState } from "react"

import { ScrollReveal, ScrollRevealItem } from "../../WebsiteBuilderV2/components/shared/ScrollReveal"

import ReviewCard from "./ReviewCard"

// Filter reviews to only include those within a time window.
// Starts at 24h and expands by 24h increments until at least minCount reviews are found.
const filterRecentReviews = (reviews, minCount = 3) => {
  if (!reviews || reviews.length === 0) return []

  const now = new Date()
  const maxDays = 365
  let hoursWindow = 24

  while (hoursWindow <= maxDays * 24) {
    const cutoff = new Date(now.getTime() - hoursWindow * 60 * 60 * 1000)
    const filtered = reviews.filter((review) => {
      const createdAt = review.createdAt || review.created_at
      if (!createdAt) return true // include if no date available
      return new Date(createdAt) >= cutoff
    })
    if (filtered.length >= minCount) return filtered
    hoursWindow += 24
  }

  // Fallback: return all reviews if we never hit minCount
  return reviews
}

const ReviewsSection = ({ reviews = [], isMobile = false }) => {
  if (!reviews || reviews.length === 0) {
    return null
  }

  const numToShow = isMobile ? 1 : 3

  // On mobile, filter reviews to recent ones (last 24h expanding)
  const availableReviews = useMemo(() => (isMobile ? filterRecentReviews(reviews, 3) : reviews), [reviews, isMobile])

  const [reviewsState, setReviewsState] = useState({
    numReviewsShown: numToShow,
    shownReviews: availableReviews.slice(0, numToShow),
    nextReviewIndex: numToShow,
    nextSlotIndex: 0
  })
  const [startAnimation, setStartAnimation] = useState(false)

  // Reset state when availableReviews or numToShow changes (e.g. mobile/desktop switch)
  useEffect(() => {
    setReviewsState({
      numReviewsShown: numToShow,
      shownReviews: availableReviews.slice(0, numToShow),
      nextReviewIndex: numToShow,
      nextSlotIndex: 0
    })
  }, [availableReviews, numToShow])

  useEffect(() => {
    if (availableReviews.length === 0) return

    const interval = setInterval(() => {
      setStartAnimation(true)
      setTimeout(() => {
        setReviewsState((prevState) => {
          const nextReviewIndex = prevState.nextReviewIndex % availableReviews.length
          const nextReview = availableReviews[nextReviewIndex]
          const newShownReviews = [...prevState.shownReviews]
          newShownReviews[prevState.nextSlotIndex] = nextReview
          return {
            ...prevState,
            shownReviews: newShownReviews,
            nextReviewIndex: (nextReviewIndex + 1) % availableReviews.length
          }
        })
      }, 650)

      setTimeout(() => {
        setStartAnimation(false)
        setReviewsState((prevState) => ({
          ...prevState,
          nextSlotIndex: (prevState.nextSlotIndex + 1) % prevState.numReviewsShown
        }))
      }, 700)
    }, 3000)
    return () => clearInterval(interval)
  }, [availableReviews])

  return (
    <ScrollReveal
      as="section"
      variant="fadeIn"
      threshold={0.08}
      className={`w-full ${isMobile ? "bg-white py-0" : "bg-white pt-14"}`}>
      <div className={`container mx-auto ${isMobile ? "w-full px-6" : "max-w-splash-container px-5"}`}>
        <ScrollRevealItem
          staggerIndex={0}
          variant="fadeUp"
          className={`mb-8 flex flex-col items-center ${isMobile ? "gap-4" : "gap-4"}`}>
          <h3
            className="text-center text-black"
            style={{
              fontFamily: '"Source Serif 4", serif',
              fontSize: isMobile ? "32px" : "40px",
              fontWeight: 700,
              lineHeight: isMobile ? "40px" : "48px",
              fontFeatureSettings: "'liga' off, 'clig' off"
            }}>
            Recent client reviews
          </h3>
        </ScrollRevealItem>

        {isMobile ? (
          <ScrollRevealItem staggerIndex={1} variant="fadeUp" className="w-full">
            {reviewsState.shownReviews[0] && (
              <ReviewCard
                review={reviewsState.shownReviews[0]}
                className={`transition-opacity duration-700 ease-in-out ${
                  startAnimation ? "opacity-0" : "opacity-100"
                }`}
              />
            )}
          </ScrollRevealItem>
        ) : (
          <div className="grid grid-cols-3 gap-6 md:grid-cols-1">
            {reviewsState.shownReviews.map((review, i) => (
              <ScrollRevealItem key={`review-slot-${i}`} staggerIndex={i + 1} variant="fadeUp">
                <ReviewCard
                  review={review}
                  className={`transition-opacity duration-700 ease-in-out ${
                    startAnimation && i === reviewsState.nextSlotIndex % reviewsState.numReviewsShown
                      ? "opacity-0"
                      : "opacity-100"
                  }`}
                />
              </ScrollRevealItem>
            ))}
          </div>
        )}
      </div>
    </ScrollReveal>
  )
}

export default ReviewsSection
